import { Injectable, inject } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { NovaBarcodeModalComponent } from "../components/nova-barcode-modal/nova-barcode-modal.component";
import { encodeNovaBarcode, NovaBarcodeType, NovaBarcodeUsage } from "../utils/nova-barcode";

/**
 * NOVA 바코드를 화면 가운데 작은 상자로 띄운다 (DOM-SYS-001)
 * <p>
 * 종류마다 메서드를 두고, 그 종류에 쓰는 용도만 받는다. 닫히면 끝나는 Promise를 돌려준다.
 */
@Injectable({ providedIn: 'root' })
export class NovaBarcodeService {

    ngbModal = inject(NgbModal);

    /**
     * VIN 바코드. 예: (click)="novaBarcode.openVin(vin, 'READ')"
     *
     * @param vin VIN 또는 짧은VIN
     * @param usage READ 단순조회, MOVE 차량이동, TASK_ALL 작업목록, ACTION 앱이 정함
     */
    openVin(vin: string, usage: 'READ' | 'MOVE' | 'TASK_ALL' | 'ACTION'): Promise<any> {
        return this.open(vin, 'VIN', usage);
    }

    /**
     * 작업번호 바코드. 예: (click)="novaBarcode.openTask(taskNo, 'TASK_UPLOAD')"
     *
     * @param taskNo 작업번호
     * @param usage READ 단순조회, TASK_UPLOAD 사진업로드, ACTION 앱이 정함
     */
    openTask(taskNo: string, usage: 'READ' | 'TASK_UPLOAD' | 'ACTION'): Promise<any> {
        return this.open(taskNo, 'TASK_NO', usage);
    }

    /**
     * VIN 바코드 문자열. 화면 영역에 np-barcode로 직접 그릴 때 쓴다. 예: [value]="novaBarcode.encodeVin(vin, 'READ')"
     *
     * @param vin VIN 또는 짧은VIN
     * @param usage READ 단순조회, MOVE 차량이동, TASK_ALL 작업목록, ACTION 앱이 정함
     * @throws 값이 비어 있을 때
     */
    encodeVin(vin: string, usage: 'READ' | 'MOVE' | 'TASK_ALL' | 'ACTION'): string {
        return encodeNovaBarcode(vin, 'VIN', usage);
    }

    /**
     * 작업번호 바코드 문자열. 화면 영역에 np-barcode로 직접 그릴 때 쓴다. 예: [value]="novaBarcode.encodeTask(taskNo, 'TASK_UPLOAD')"
     *
     * @param taskNo 작업번호
     * @param usage READ 단순조회, TASK_UPLOAD 사진업로드, ACTION 앱이 정함
     * @throws 값이 비어 있을 때
     */
    encodeTask(taskNo: string, usage: 'READ' | 'TASK_UPLOAD' | 'ACTION'): string {
        return encodeNovaBarcode(taskNo, 'TASK_NO', usage);
    }

    /** 생성기가 오류를 내면 상자 안에 메시지를 보여 준다 */
    private open(value: string, type: NovaBarcodeType, usage: NovaBarcodeUsage): Promise<any> {
        const modal = this.ngbModal.open(NovaBarcodeModalComponent, { centered: true, modalDialogClass: 'nova-barcode-modal-dialog' });
        try {
            modal.componentInstance.barcode = encodeNovaBarcode(value, type, usage);
        } catch (e: any) {
            modal.componentInstance.error = e?.message ?? String(e);
        }
        // 바깥 클릭·Esc로 닫혀도 쓰는 쪽에서 오류가 나지 않게 한다
        return modal.result.catch(() => undefined);
    }
}
